/**
 * Main app layout with navigation.
 */

import { Link, useLocation, Outlet } from "react-router-dom";
import { useNostrUser } from "@/hooks/useNostr";
import { Home, Trophy, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

export function Layout() {
  const { user, isLoggedIn, logout } = useNostrUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top navigation */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 font-bold text-foreground">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
              <span className="text-sm font-black text-primary">1G</span>
            </div>
            <span className="hidden sm:inline">One Group</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 sm:flex">
            {isLoggedIn && navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="flex items-center gap-3">
            {isLoggedIn && user ? (
              <>
                {user.profile?.picture && (
                  <img
                    src={user.profile.picture}
                    alt=""
                    className="h-7 w-7 rounded-full border border-border"
                  />
                )}
                <span className="hidden text-sm text-foreground sm:inline">
                  {user.profile?.display_name || user.profile?.name || user.pubkey.slice(0, 8)}
                </span>
                <button
                  onClick={() => logout()}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : null}

            {/* Mobile menu toggle */}
            <button
              className="rounded-lg p-2 text-muted-foreground hover:bg-accent sm:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileMenuOpen && isLoggedIn && (
          <nav className="border-t border-border px-4 py-2 sm:hidden">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                  isActive(item.path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener"
          className="underline hover:text-foreground"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
}